import config from '@/Constants';
import { useAnswerHistoryConfig } from '@/components_v2/WebForm/context/AnswerHistoryContext';
import { AxiosHelper } from '@/modules/shared/infra/protocols/http';
import {
  Box,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  List,
  ListItem,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import React from 'react';
import { FiClock, FiSearch } from 'react-icons/fi';

type HistoryItem = {
  id: string;
  value: string;
  answeredAt: number;
};

// answeredAt can arrive as a Unix timestamp in seconds (backend) instead of
// milliseconds — treating seconds as ms lands near epoch, e.g. "há mais de 56 anos".
const MS_TIMESTAMP_THRESHOLD = 1e12;

function toMs(answeredAt: number): number {
  return answeredAt < MS_TIMESTAMP_THRESHOLD ? answeredAt * 1000 : answeredAt;
}

function formatAnsweredAt(answeredAt: number): string {
  if (!answeredAt) return 'Sem data';

  return formatDistanceToNow(new Date(toMs(answeredAt)), {
    addSuffix: true,
    locale: ptBR,
  });
}

type Props = {
  questionId: string;
  formularyId?: string;
  onSelect: (value: string) => void;
};

const AnswerHistory: React.FC<Props> = ({ questionId, formularyId, onSelect }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { disabled } = useAnswerHistoryConfig();
  const [items, setItems] = React.useState<HistoryItem[]>([]);
  const [search, setSearch] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const searchRef = React.useRef<HTMLInputElement>(null);

  const fetchHistory = React.useCallback(
    async (searchTerm = '') => {
      if (!formularyId) return;
      setIsLoading(true);
      try {
        const params = new URLSearchParams({
          question_id: questionId,
          formulary_id: formularyId,
          origin: 'web',
        });
        if (searchTerm) params.set('search', searchTerm);

        const response = await AxiosHelper.get<HistoryItem[]>({
          url: `${config.QUESTION_ANSWERS_URL}?${params.toString()}`,
        });

        if (response.statusCode >= 200 && response.statusCode < 300) {
          const sorted = [...(response.body || [])].sort(
            (a, b) => toMs(b.answeredAt) - toMs(a.answeredAt),
          );
          setItems(sorted);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [questionId, formularyId],
  );

  const debouncedFetch = React.useMemo(() => {
    let timer: ReturnType<typeof setTimeout>;
    return (term: string) => {
      clearTimeout(timer);
      timer = setTimeout(() => fetchHistory(term), 300);
    };
  }, [fetchHistory]);

  const handleOpen = React.useCallback(() => {
    onOpen();
    setSearch('');
    fetchHistory();
  }, [onOpen, fetchHistory]);

  const handleSearch = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const term = e.target.value;
      setSearch(term);
      debouncedFetch(term);
    },
    [debouncedFetch],
  );

  const handleSelect = React.useCallback(
    (value: string) => {
      onSelect(value);
      onClose();
    },
    [onSelect, onClose],
  );

  React.useEffect(() => {
    if (isOpen) {
      const id = setTimeout(() => searchRef.current?.focus(), 50);
      return () => clearTimeout(id);
    }
  }, [isOpen]);

  if (disabled || !formularyId) return null;

  return (
    <Popover isOpen={isOpen} onClose={onClose} placement="bottom-end" isLazy>
      <PopoverTrigger>
        <IconButton
          aria-label="Histórico de respostas"
          icon={<FiClock />}
          size="sm"
          variant="ghost"
          onClick={handleOpen}
          ml="1"
          flexShrink={0}
          alignSelf="flex-start"
          mt="1"
        />
      </PopoverTrigger>
      <PopoverContent w="280px" shadow="lg">
        <PopoverBody p="2">
          <InputGroup size="sm" mb="2">
            <InputLeftElement pointerEvents="none">
              <Box color="gray.400">
                <FiSearch size={12} />
              </Box>
            </InputLeftElement>
            <Input
              ref={searchRef}
              placeholder="Buscar..."
              value={search}
              onChange={handleSearch}
            />
          </InputGroup>
          {isLoading ? (
            <Flex justify="center" py="4">
              <Spinner size="sm" />
            </Flex>
          ) : items.length === 0 ? (
            <Text color="gray.400" fontSize="sm" textAlign="center" py="3">
              Nenhum histórico encontrado
            </Text>
          ) : (
            <List maxH="200px" overflowY="auto" spacing="1">
              {items.map((item) => (
                <ListItem
                  key={item.id}
                  px="2"
                  py="1.5"
                  rounded="md"
                  cursor="pointer"
                  _hover={{ bg: 'gray.100' }}
                  onClick={() => handleSelect(item.value)}
                >
                  <Text fontSize="sm" noOfLines={1}>
                    {item.value}
                  </Text>
                  <Text fontSize="xs" color="gray.400">
                    {formatAnsweredAt(item.answeredAt)}
                  </Text>
                </ListItem>
              ))}
            </List>
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default AnswerHistory;
